import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_opport-biz-ma/artifacts/oknd9jsa_Logo%20Club%20Business%20Maroc%20Annuaire.png";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    if (form.password.length < 8) { setError("Mot de passe trop court (min. 8 caractères)"); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password: form.password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #081026 0%, #0D1B3E 50%, #081530 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-5" style={{ backgroundImage: "radial-gradient(circle, #C9A227 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative z-10 w-full max-w-sm mx-4 animate-fade-in-up">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Club Business Maroc" className="w-24 h-24 object-contain rounded-xl mb-2" />
        </div>
        <div className="cbm-glass rounded-2xl p-8 shadow-card">
          {success ? (
            <div className="text-center">
              <CheckCircle2 size={40} className="text-green-400 mx-auto mb-3" />
              <h2 className="font-outfit font-semibold text-white text-xl mb-2">Mot de passe réinitialisé !</h2>
              <p className="text-cbm-secondary text-sm mb-4">Vous allez être redirigé vers la connexion...</p>
              <Link to="/login" className="cbm-btn-primary text-sm">Se connecter</Link>
            </div>
          ) : (
            <>
              <h1 className="font-outfit text-2xl font-semibold text-white mb-1">Nouveau mot de passe</h1>
              <p className="text-cbm-muted text-sm mb-6">Choisissez un nouveau mot de passe pour votre compte.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="cbm-label block mb-1.5">Nouveau mot de passe *</label>
                  <div className="relative">
                    <input data-testid="reset-password" type={showPwd ? "text" : "password"} className="cbm-input pr-10" placeholder="Min. 8 caractères" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-cbm-secondary" onClick={() => setShowPwd(!showPwd)}>
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="cbm-label block mb-1.5">Confirmer *</label>
                  <input data-testid="reset-confirm" type="password" className="cbm-input" placeholder="Répétez votre mot de passe" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
                </div>
                {error && <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm" data-testid="reset-error">{error}</div>}
                <button data-testid="reset-submit" type="submit" disabled={submitting} className="cbm-btn-primary w-full justify-center text-sm py-2.5">
                  {submitting ? <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Réinitialisation...</> : "Réinitialiser"}
                </button>
              </form>
              <Link to="/login" className="block text-center text-cbm-muted text-xs mt-4 hover:text-cbm-secondary">Retour à la connexion</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
